import t from 'tcomb-form';
import * as R from 'ramda';

import {prettyDollars} from 'js/common/string_helper';
import {enums} from './global';

const isAdmin = !!window.sn.isAdmin;
const isSaasCampaign = !!R.path(['sn', 'flags', 'isSaasCampaign'], window);
const brands = t.Object.is(window.sn.brands) ? window.sn.brands : {};

const {
    CampaignAudienceLocationMap,
    CampaignAudienceGenderMap,
    CampaignObjective,
    CampaignObjectiveMap,
    CampaignAudienceAgeMap,
    VideoOrientationMap,
    CompensationTypeMap,
    CampaignDisplayTypeMap,
    ContentUseMap
} = enums;

const CampaignAudienceLocationEnum = t.enums(
    CampaignAudienceLocationMap,
    'CampaignAudienceLocation'
);
const CampaignAudienceGenderEnum = t.enums(CampaignAudienceGenderMap, 'CampaignAudienceGender');
const CampaignAudienceAgeEnum = t.enums(CampaignAudienceAgeMap, 'CampaignAudienceAge');
const CampaignObjectiveEnum = t.enums(CampaignObjectiveMap, 'CampaignObjective');
const VideoOrientationEnum = t.enums(VideoOrientationMap, 'VideoOrientation');
const CompensationTypeEnum = t.enums(CompensationTypeMap, 'CompensationType');
const CampaignDisplayTypeEnum = t.enums(CampaignDisplayTypeMap, 'CampaignDisplayType');
const ContentUseEnum = t.enums(ContentUseMap, 'ContentUse');

const BudgetOptions = {
    10000: '$10,000',
    30000: '$30,000',
    50000: '$50,000',
    100000: '$100,000'
};

export const DefaultInterests = [
    'Beauty',
    'DIY',
    'Entertainment',
    'Family',
    'Fashion',
    'Fitness',
    'Food',
    'Lifestyle',
    'Pets',
    'Photography',
    'Social Good',
    'Travel'
];

const MIN_BUDGET = 0;
const MinBudgetValidator = t.refinement(t.Number, n => n > MIN_BUDGET);
const BudgetValidator = t.union([t.enums(BudgetOptions, 'Budget'), MinBudgetValidator]);
BudgetValidator.getValidationErrorMessage = () =>
    `- Budget must be greater than ${prettyDollars(MIN_BUDGET, 0)}`;

const MIN_BID_CPCC = isSaasCampaign ? 50 : 0;
const MIN_BID_CPE = isSaasCampaign ? 0.06 : 0;
const cpccBidValidator = t.refinement(t.Number, n => n >= MIN_BID_CPCC);
cpccBidValidator.getValidationErrorMessage = () => `- Cost Per Piece
    Of Content must be at least ${prettyDollars(MIN_BID_CPCC, 0)}`;
const cpeBidValidator = t.refinement(t.Number, n => n >= MIN_BID_CPE);
cpeBidValidator.getValidationErrorMessage = () =>
    `- Cost Per Engagement must be at least ${prettyDollars(MIN_BID_CPE, 1)}`;
const BidValidator = objective => {
    if (objective === CampaignObjective.CONTENT_CREATION) {
        return cpccBidValidator;
    }
    return cpeBidValidator;
};

export const getKpiFromObjective = objective =>
    R.propOr('N/A', objective, {
        [CampaignObjective.SOCIAL_ENGAGEMENT]: 'CPE (Cost Per Engagement)',
        [CampaignObjective.CONTENT_CREATION]: 'CPCC (Cost Per Content Created)',
        [CampaignObjective.AWARENESS]: 'CPM (Cost Per 1,000 Impressions)'
    });

export const getStep1Props = () =>
    t.struct({
        brand_id: t.enums(brands, 'Brand'),
        type: CampaignDisplayTypeEnum,
        objective: CampaignObjectiveEnum,
        content_use: ContentUseEnum
    });

export function getStep2Props(formData) {
    const schema = {
        audience_interests: t.String,
        audience_location: CampaignAudienceLocationEnum,
        audience_location_custom_cities: t.maybe(t.list(t.Obj)),
        audience_location_custom_countries: t.maybe(t.list(t.Obj)),
        audience_gender: CampaignAudienceGenderEnum,
        audience_age: t.list(CampaignAudienceAgeEnum),
        audience_languages: t.String,
        age_21_plus: t.maybe(t.Boolean),
        influencer_family_friendly: t.maybe(t.Boolean),
        creator_fraud_detection_enabled: t.maybe(t.Boolean),
        budget_and_bid: t.maybe(
            t.struct({
                budget: BudgetValidator,
                bid: BidValidator(formData.objective)
            })
        )
    };

    return t.struct(schema);
}

export const Guideline = t.struct({
    guideline: t.String,
    should: t.Number
});

export function getStep3Props(formData, hideInfluencerItems) {
    const schema = {
        name: t.String,
        short_description: t.String,
        guidelines: t.maybe(t.list(Guideline)),
        screen_names: t.maybe(t.String),
        sample_post_filename: t.maybe(t.String),
        video_orientation: VideoOrientationEnum,
        sample_post_images: t.maybe(t.list(t.maybe(t.Object))),
        sample_post_videos: t.maybe(t.list(t.maybe(t.Object))),
        additional_documents: t.maybe(t.String),
        influencer_compensation_type: CompensationTypeEnum,
        product_name: t.maybe(t.refinement(t.String, n => n.length <= 250)),
        product_description: t.maybe(t.String),
        product_retail_cost: t.maybe(t.Number),
        hashtags: t.subtype(t.String, s => isAdmin || /#?\b(sponsored|sp|ad)\b/i.test(s)),
        flight_start: t.refinement(t.Date, fs => formData.flight_end && fs < formData.flight_end),
        flight_end: t.Date
    };

    if (formData && formData.objective === CampaignObjective.DRIVE_SALES) {
        schema.caption_include_promo_code = t.Boolean;
    }

    if (hideInfluencerItems) {
        delete schema.hashtags;
        delete schema.screen_names;
    }

    return t.struct(schema);
}



// WEBPACK FOOTER //
// ./create_campaign_wizard/constants/edit_campaign_types.js